import { useCallback } from 'react'
import { Cargando, Problema, Vacio } from '../componentes/Estados.js'
import { Icono } from '../componentes/Icono.js'
import { PastillaTorneo } from '../componentes/Pastilla.js'
import { useCarga } from '../carga.js'
import { obtenerRepositorio } from '../datos/index.js'
import type { MiTorneo } from '../datos/index.js'
import { conSigno, fechaCorta, plural, puesto, signoDe } from '../formato.js'
import { ir } from '../navegacion.js'
import type { Vista } from '../navegacion.js'
import { EnVivo } from './EnVivo.js'
import type { Grupo } from './EnVivo.js'

/**
 * La pestaña «Torneos» del que tiene sesión: los suyos y la vitrina del club.
 *
 * Son cuatro segmentos: los suyos primero, y después los mismos tres grupos que
 * ve el que entra sin cuenta (en vivo, próximos, resultados). Antes eran
 * pestañas aparte del menú, y la vitrina repetía lo que se juega. Ahora es un
 * solo lugar para buscar un torneo, y en la vitrina los suyos salen marcados.
 *
 * Cada segmento es una vista con su propia URL, así el botón atrás vuelve al
 * segmento de antes y un enlace de la vitrina sin cuenta cae en el suyo.
 */

export type Segmento = 'tuyos' | Grupo

const SEGMENTOS: readonly { id: Segmento; texto: string; vista: Vista }[] = [
  { id: 'tuyos', texto: 'Tuyos', vista: { nombre: 'torneos' } },
  { id: 'vivo', texto: 'En vivo', vista: { nombre: 'vivo' } },
  { id: 'proximos', texto: 'Próximos', vista: { nombre: 'proximos' } },
  { id: 'resultados', texto: 'Resultados', vista: { nombre: 'resultados' } },
]

export function Torneos({ segmento }: { segmento: Segmento }) {
  return (
    <div className="pantalla">
      <h1>Torneos</h1>
      <nav className="segmentos" aria-label="Qué torneos ver">
        {SEGMENTOS.map((s) => (
          <button
            key={s.id}
            className={segmento === s.id ? 'activo' : ''}
            aria-current={segmento === s.id ? 'page' : undefined}
            onClick={() => ir(s.vista)}
          >
            {s.texto}
          </button>
        ))}
      </nav>

      {/* La llave hace que cada segmento arranque de cero: sin ella, pasar de
          uno a otro mostraría un instante la lista anterior. */}
      {segmento === 'tuyos' ? (
        <Tuyos />
      ) : (
        <EnVivo key={segmento} sinCuenta={false} grupo={segmento} />
      )}
    </div>
  )
}

/**
 * Sus torneos, agrupados por momento y no ordenados por fecha a secas: «se está jugando»,
 * «viene» y «pasó» son tres cosas que se buscan por motivos distintos, y una
 * sola lista cronológica obliga a leer la fecha de cada uno para saber en cuál
 * de los tres casos está parado.
 */

const GRUPOS = [
  { titulo: 'Jugándose ahora', de: (t: MiTorneo) => t.estado === 'en_curso' },
  /* Sin el borrador, igual que «Hoy»: es el torneo que el organizador todavía
     está armando, y puede cambiar o no jugarse. */
  { titulo: 'Lo que viene', de: (t: MiTorneo) => t.estado === 'inscripcion' },
  { titulo: 'Jugados', de: (t: MiTorneo) => t.estado === 'finalizado' },
  { titulo: 'Cancelados', de: (t: MiTorneo) => t.estado === 'cancelado' },
]

function Tuyos() {
  const pedir = useCallback(async () => {
    const repo = await obtenerRepositorio()
    return (await repo.listarMisTorneos()).filter((t) => GRUPOS.some((g) => g.de(t)))
  }, [])

  const { datos, error, cargando, recargar } = useCarga(pedir)

  if (error) return <Problema mensaje={error} alReintentar={recargar} />
  if (!datos) return cargando ? <Cargando que="Buscando tus torneos…" /> : null

  if (datos.length === 0) {
    return (
      <Vacio titulo="Todavía no estás anotado en ninguno">
        <p>Cuando el organizador te sume a la nómina de un torneo, aparece acá.</p>
      </Vacio>
    )
  }

  return (
    <div className="pantalla">
      {GRUPOS.map((grupo) => {
        const suyos = datos.filter(grupo.de)
        if (suyos.length === 0) return null

        return (
          <section className="seccion" key={grupo.titulo}>
            <h3 className="seccion-titulo">{grupo.titulo}</h3>
            <ul className="lista-torneos">
              {suyos.map((t) => (
                <li key={t.id}>
                  <button onClick={() => ir({ nombre: 'torneo', id: t.id, pestana: 'mesa' })}>
                    <span className="lista-principal">
                      <b>{t.nombre}</b>
                      <span className="tenue">
                        {fechaCorta(t.fecha)}
                        {t.sede && ` · ${t.sede}`}
                      </span>
                      <span className="lista-datos">
                        {t.mi ? (
                          <>
                            <span className="destacado">{puesto(t.mi.posicion)}</span>
                            <span className="tenue">de {t.participantes}</span>
                            <span className="ganadas">{t.mi.ganadas}G</span>
                            <span className="perdidas">{t.mi.perdidas}P</span>
                            <span className={`efectividad ${signoDe(t.mi.efectividad)}`}>
                              {conSigno(t.mi.efectividad)}
                            </span>
                          </>
                        ) : (
                          <span className="tenue">
                            {plural(t.participantes, 'anotado', 'anotados')}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="lista-derecha">
                      <PastillaTorneo estado={t.estado} />
                      <Icono nombre="flecha" tamano={18} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}
